import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ReportDataDialog } from '@unified-ui/utilities';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { Observable } from 'rxjs';
import { DialogUserFoldersComponent } from '../dialog-user-folders/dialog-user-folders.component';
import { AsyncPipe } from '@angular/common';

/**
 * Shared component to handle save dialogs when closing an unsaved or active saving a report.
 * The first dialog confirms the closing without saving, the second one performs the
 * saving action with share options.
 * This component is responsible for receiving the information and preparing the data
 * to be saved, handle the visual part of the dialogs and emit an event for closing the tab.
 * The saving itself is made by the feature components, since the APIs are specific.
 */
@Component({
    selector: 'save-report-dialog-component',
    standalone: true,
    templateUrl: './save-report-dialog.component.html',
    styleUrl: './save-report-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogModule, ButtonModule, TextareaModule, InputTextModule, FormsModule, CheckboxModule, DialogUserFoldersComponent, AsyncPipe]
})
export class SaveReportDialogComponent {
    /** Report information */
    public readonly report = input.required<ReportDataDialog>();
    /** Original report data */
    protected readonly originalReport = computed(() => this.report());
    /** Report data with editing of name and description for saving */
    protected readonly reportDataForSaving = computed<ReportDataDialog>(() => {
        const report = this.report();
        if (!report) return { name: '', description: '' };
        if (report.isOwner) {
            return structuredClone(report);
        } else {
            return {
                ...report,
                reportId: undefined,
                userName: undefined
            };
        }
    });
    /** Visibility of closing an unsaved tab */
    public readonly isClosingConfirmationVisible = input(false);
    protected readonly isClosingConfirmationVisibleState = linkedSignal(() => this.isClosingConfirmationVisible());
    /** Visibility of save dialog */
    public readonly isSavingConfirmationVisible = input(false);
    protected readonly isSavingConfirmationVisibleState = linkedSignal(() => this.isSavingConfirmationVisible());

    /** Optional checking for scheduled reference */
    public readonly isReportReferencedInOthersSchedule$ = input<Observable<boolean | undefined> | undefined>(undefined);
    /** Indicates if it closes without saving */
    public readonly closeReportWithoutSaving = output();
    /** Cancels closing an unsaved report */
    public readonly cancelClosingUnsaved = output();
    /** Save report emitter */
    public readonly saveReport = output<ReportDataDialog>();
    /** Save report As (copy) emitter */
    public readonly saveReportAs = output<ReportDataDialog>();

    /** Click for delete (save: false) or save an unsaved report */
    protected closeUnsavedTab(save: boolean) {
        if (save) {
            this.isSavingConfirmationVisibleState.set(true);
        } else {
            this.closeReportWithoutSaving.emit();
            this.isSavingConfirmationVisibleState.set(false);
        }
        this.isClosingConfirmationVisibleState.set(false);
    }

    /** Click in cancel button */
    protected cancelCloseUnsaved() {
        this.cancelClosingUnsaved.emit();
        this.isClosingConfirmationVisibleState.set(false);
        this.isSavingConfirmationVisibleState.set(false);
    }

    /**
     * Gets whether is save disabled
     * Cases: the name is not valid
     */
    protected get isSaveDisabled(): boolean {
        return this.reportDataForSaving().name === undefined || this.reportDataForSaving().name?.length === 0;
    }

    /**
     * Gets whether is save as disabled
     * Cases: the report name didn't change, the name is not valid
     */
    protected get isSaveAsDisabled(): boolean {
        const isReportNameChanged = this.reportDataForSaving().name !== this.originalReport.name;
        return !isReportNameChanged || this.reportDataForSaving().name?.length === 0;
    }

    /**
     * Emits the data to save
     */
    protected save() {
        this.saveReport.emit(this.reportDataForSaving());
        this.isSavingConfirmationVisibleState.set(false);
    }

    /**
     * Emits the data to save as
     */
    protected saveAs() {
        this.saveReportAs.emit(this.reportDataForSaving());
        this.isSavingConfirmationVisibleState.set(false);
    }

    /**
     * Set the folder id in save criteria
     * @param folderId
     */
    protected setSelectedFolder(folderId: string | null) {
        this.reportDataForSaving().folderId = folderId;
    }
}
